import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import api from "../api";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";

export function CmsPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    api.get(`/public/pages/${slug}`).then((r) => setPage(r.data)).catch(() => setMissing(true));
  }, [slug]);
  if (missing) return <div className="max-w-3xl mx-auto px-4 py-20 text-center text-sm text-zinc-500">Page not found.</div>;
  if (!page) return null;
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14" data-testid={`cms-page-${slug}`}>
      <Seo title={page.seo_title || `${page.title} | CourierRadar`} description={page.seo_description} noindex={page.noindex} />
      <h1 className="text-4xl font-extrabold tracking-tight text-black mb-6">{page.title}</h1>
      <div className="text-sm sm:text-base text-zinc-600 leading-relaxed whitespace-pre-line">{page.body}</div>
    </div>
  );
}

export function FaqPage() {
  const [faqs, setFaqs] = useState([]);
  useEffect(() => { api.get("/public/home").then((r) => setFaqs(r.data.faqs || [])).catch(() => {}); }, []);
  const jsonLd = faqs.length ? {
    "@context": "https://schema.org", "@type": "FAQPage",
    "mainEntity": faqs.map((f) => ({ "@type": "Question", "name": f.question, "acceptedAnswer": { "@type": "Answer", "text": f.answer } })),
  } : null;
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14" data-testid="faq-page">
      <Seo title="FAQ | CourierRadar" description="Answers about CourierRadar coverage, vetting, tracking and integrations." jsonLd={jsonLd} />
      <h1 className="text-4xl font-extrabold tracking-tight text-black mb-8">Frequently asked questions</h1>
      <div className="space-y-3">
        {faqs.map((f) => (
          <details key={f.id} data-testid={`faq-${f.id}`} className="cr-card !p-5 group">
            <summary className="text-sm font-bold cursor-pointer list-none flex justify-between items-center">{f.question}<span className="text-[#0A0A0A] group-open:rotate-45 transition-transform text-lg leading-none">+</span></summary>
            <p className="text-sm text-zinc-600 mt-3 leading-relaxed">{f.answer}</p>
          </details>
        ))}
      </div>
    </div>
  );
}

export function PublicLoadsPage() {
  return <Navigate to="/find-jobs" replace />;
}

export function PublicLoadDetail() {
  const { user } = useAuth();
  const { ref } = useParams();
  const [job, setJob] = useState(null);
  useEffect(() => { api.get(`/public/jobs/${ref}`).then((r) => setJob(r.data)).catch(() => {}); }, [ref]);
  if (!job) return null;
  const title = `Courier job ${job.ref}: ${job.pickup?.city || ""} to ${job.delivery?.city || ""} | CourierRadar`;
  const jsonLd = {
    "@context": "https://schema.org", "@type": "Service", "name": `Courier delivery ${job.ref}`,
    "areaServed": [job.pickup?.country, job.delivery?.country].filter(Boolean),
    "provider": { "@type": "Organization", "name": "CourierRadar" },
  };
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14" data-testid="load-detail">
      <Seo title={title} description={`Open courier job ${job.ref} from ${job.pickup?.city || ""} to ${job.delivery?.city || ""}. ${(job.vehicle_type || "").replace("_", " ")}.`} jsonLd={jsonLd} />
      <p className="font-mono text-xs text-zinc-400">{job.ref}</p>
      <h1 className="text-3xl font-extrabold tracking-tight mt-1">{job.pickup?.city}, {job.pickup?.country} → {job.delivery?.city}, {job.delivery?.country}</h1>
      <p className="text-sm text-zinc-600 mt-3 capitalize">{(job.service_level || "").replace("_", " ")} · {(job.vehicle_type || "").replace("_", " ")}
        {job.cargo?.weight_kg ? ` · ${job.cargo.weight_kg} kg` : ""}{job.cargo?.pallets ? ` · ${job.cargo.pallets} pallets` : ""}</p>
      <p className="text-sm text-zinc-500 mt-1">Pickup: {job.pickup_date || "flexible"}</p>
      {!user && (
        <Link to="/auth?mode=register&role=courier" data-testid="load-signup-cta"
          className="inline-block mt-6 bg-[#0A0A0A] text-white hover:bg-[#26272A] font-semibold transition-all px-6 py-3 rounded-lg text-sm">
          Sign up as a courier to bid
        </Link>
      )}
      {user?.role === "courier" && (
        <Link to="/find-jobs" data-testid="load-board-cta"
          className="inline-block mt-6 bg-[#1C1B1A] text-white hover:bg-[#2C2B2A] font-semibold transition-all px-6 py-3 rounded-full text-sm">
          Open the load board
        </Link>
      )}
    </div>
  );
}
